//Auth.js
import { auth, db } from "./config";

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updatePassword,
  sendPasswordResetEmail,
  signOut,
  sendEmailVerification,
  updateProfile,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  collection,
  updateDoc,
  addDoc,
} from "firebase/firestore";
//import { httpsCallable } from "firebase/functions"; // Import firebase functions
//import { functions } from "./config"; // Ensure functions is exported from config.js

// ---- Functions endpoints (CRA reads from .env) ----
const FUNCTIONS_BASE =
  process.env.REACT_APP_FUNCTIONS_BASE ||
  "https://us-central1-employee-b47f3.cloudfunctions.net"; // safe fallback for staging

const CREATE_STRIPE_CUSTOMER_URL =
  process.env.REACT_APP_CREATE_STRIPE_CUSTOMER_URL ||
  `${FUNCTIONS_BASE}/createStripeCustomer`;

// Secure Forgot Password: Send Reset Link via Email
export async function resetPassword(email) {
  try {
    await sendPasswordResetEmail(auth, email);
    console.log("Password reset email sent to:", email);
  } catch (err) {
    console.error("Error sending password reset email:", err.message);
    throw new Error(
      "Failed to send password reset email. Make sure the email is registered.",
    );
  }
}

// First-Time Login: Employee Updates Their Password
export async function updateEmployeePassword(uid, newPassword, companyId) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated.");

    await updatePassword(user, newPassword); // Update Firebase Auth password

    // 🔹 Mark employee as active inside the company’s subcollection
    if (companyId) {
      await updateDoc(doc(db, `companies/${companyId}/employees`, uid), {
        passwordSet: true,
      });
    } else {
      throw new Error("Company ID is required.");
    }

    console.log("Password updated successfully.");
  } catch (err) {
    console.error("Error updating password:", err.message);
    throw new Error("Failed to update password.");
  }
}

// Register Employer
// 1) Create Auth user + send email verification (no Stripe/Firestore yet)
export async function registerUser(
  email,
  password,
  role, // "employer"
  name,
  phone,
  companyRole,
  referralSource,
) {
  const userCredential = await createUserWithEmailAndPassword(
    auth,
    email,
    password,
  );
  const user = userCredential.user;

  // ✅ Set the display name BEFORE sending the verification email
  const safeName = (name || "").trim() || email.split("@")[0];
  await updateProfile(user, { displayName: safeName });

  await sendEmailVerification(user, {
    url: `${window.location.origin}/verify-email`, // they'll land back here
    handleCodeInApp: false,
  });

  // We return the user; the rest will happen AFTER verification.
  return user;
}

// 2) After email is verified, create Stripe customer + write Firestore
export async function finalizeEmployerAfterEmailVerified({
  name,
  email,
  phone,
  companyRole,
  referralSource,
}) {
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated.");
  await user.reload();
  if (!user.emailVerified) throw new Error("Email not verified.");

  // Create Stripe customer (your existing endpoint)
  // Create Stripe customer (secured: sends Firebase ID token)
  const idToken = await auth.currentUser.getIdToken();

  const response = await fetch(CREATE_STRIPE_CUSTOMER_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ email, name }),
  });

  const customerResponse = await response.json();
  if (!customerResponse.customerId)
    throw new Error("Failed to create Stripe customer.");

  // Write to Firestore
  await setDoc(doc(db, "customers", user.uid), {
    stripeId: customerResponse.customerId,
    email,
    name,
    role: "employer",
    phone,
    companyRole,
    referralSource,
    createdAt: new Date(),
    subscriptions: {},
  });

  await setDoc(doc(db, "users", user.uid), {
    email,
    role: "employer",
    name,
    phone,
    companyRole,
    referralSource,
    companyId: null,
    createdAt: new Date(),
    onboarding: {
      step: "company-setup",
      emailVerified: true,
      companyCreated: false,
      subscriptionStatus: "none",
      lastUpdatedAt: new Date(),
    },
  });

  return true;
}

// Create a new company and link it to the employer
export async function createCompany(
  companyName,
  industry,
  location,
  ownerId,
  ownerEmail,
) {
  try {
    const companyRef = collection(db, "companies");
    const newCompany = await addDoc(companyRef, {
      name: companyName,
      industry,
      location,
      ownerId,
      ownerEmail,
      employees: [],
      createdAt: new Date(),
    });

    // Link the company to the employer in Firestore
    await updateDoc(doc(db, "users", ownerId), { companyId: newCompany.id });

    return newCompany.id;
  } catch (err) {
    console.error("Company Creation Error:", err.message);
    throw new Error("Failed to create company.");
  }
}

// 🔹 Authenticate User (Employers & Employees)
export async function loginUser(email, password) {
  const userCredential = await signInWithEmailAndPassword(
    auth,
    email,
    password,
  );
  const user = userCredential.user;

  // ✅ single source of truth for both employer + employee
  const snap = await getDoc(doc(db, "users", user.uid));

  if (!snap.exists()) {
    throw new Error("User profile not found. Please contact support.");
  }

  return { ...snap.data(), uid: user.uid };
}

// Logout User
export function logoutUser() {
  return signOut(auth);
}

// Get User Details (Employer or Employee)
export async function getUserDetails(uid) {
  try {
    // 🔹 First, check the `users` collection (for employers)
    const userRef = doc(db, "users", uid);
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) return { ...userDoc.data(), uid };

    // 🔹 If not found, check the employees' sub-collections inside `companies`
    console.log(
      "User not found in `users`. Searching in `employees` sub-collections...",
    );

    const companiesRef = collection(db, "companies");
    const companySnapshot = await getDocs(companiesRef);

    for (const companyDoc of companySnapshot.docs) {
      const companyId = companyDoc.id;
      const employeeRef = doc(db, `companies/${companyId}/employees`, uid);
      const employeeDoc = await getDoc(employeeRef);

      if (employeeDoc.exists()) {
        console.log(`Employee found in company ${companyId}`);
        return { ...employeeDoc.data(), uid, companyId };
      }
    }

    console.error("User not found in Firestore.");
    throw new Error("User not found.");
  } catch (err) {
    console.error("Error fetching user details:", err.message);
    return null;
  }
}
